import { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { updateAppointmentsDetailsWithAvailability } from '~/analytics/analytics.utils';
import { triggerAvailabilityInteractionAnalytics } from '~/analytics/analytics.listener';

import { CalendarHeader } from './calendar-header';
import { CalendarWeek } from './calendar-week';
import { CalendarDateSlots } from './calendar-date-slots';

import { dateUtils } from '../../../routes/utils';
import { findDateSlot } from '../helpers';

import styles from './Calendar.scss';
import { apiHelpers } from '~/api';

export class Calendar extends PureComponent {
  static propTypes = {
    months: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.number.isRequired,
        year: PropTypes.number.isRequired,
        dates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      }),
    ),
    selectedDateSlot: PropTypes.string.isRequired,
    selectDateSlot: PropTypes.func.isRequired,
    maxAdvance: PropTypes.number.isRequired,
    apiCallFinished: PropTypes.func.isRequired,
    isApiCallFinished: PropTypes.bool.isRequired,
    loadNextSlots: PropTypes.func,
    loadPreviousSlots: PropTypes.func,
    eligibleSlotsFilter: PropTypes.func.isRequired,
    selectedPartnerId: PropTypes.string,
  };

  static defaultProps = {
    months: [],
    loadNextSlots: undefined,
    loadPreviousSlots: undefined,
    selectedPartnerId: undefined,
  };

  static isWaitingForSlotsToLoad(months, date) {
    return date && !Calendar.getMonthByDate(months, date);
  }

  static getMonthByDate(months, date, monthOffset = 0) {
    const dateToFind = new Date(date.getFullYear(), date.getMonth() + monthOffset);
    return months.find(
      (month) => month.year === dateToFind.getFullYear() && month.month === dateToFind.getMonth(),
    );
  }

  static isMonthTheSameWithDate(month, date) {
    return month.year === date.getFullYear() && month.month === date.getMonth();
  }

  static equalsDate(date1, date2) {
    return +date1 === +date2;
  }

  static getMonthIndexByDate(months, date) {
    return date
      ? Calendar.getMonthIndexByMonth(months, { year: date.getFullYear(), month: date.getMonth() })
      : 0;
  }

  static getMonthIndexByMonth(months, currentMonth) {
    return months.findIndex(
      ({ year, month }) => currentMonth.year === year && currentMonth.month === month,
    );
  }

  constructor(props) {
    super(props);
    const { months, selectedDateSlot } = props;
    this.state = {
      months: [],
      selectedDateSlot: '',
    };

    if (!selectedDateSlot) {
      const isSlotsAvailableInCurrentMonth =
        months.length !== 0 && months[0].dates.some((date) => date.times.length);
      const isSlotsAvailableInFollowingMonth =
        !isSlotsAvailableInCurrentMonth &&
        months.length > 1 &&
        months[1].dates.some((date) => date.times.length);
      let selectedMonthDate = new Date(months[0].year, months[0].month);
      if (isSlotsAvailableInFollowingMonth) {
        selectedMonthDate = new Date(months[1].year, months[1].month);
      }

      this.state = {
        ...this.state,
        currentMonth: months[0],
        selectedMonthDate,
      };
    }
  }

  static getDerivedStateFromProps(props, state) {
    const {
      months,
      selectedDateSlot,
      apiCallFinished,
      loadPreviousSlots,
      loadNextSlots,
      eligibleSlotsFilter,
      maxAdvance,
      selectedPartnerId,
      isApiCallFinished,
    } = props;
    const loadNewMonthsSlots = JSON.stringify(state.months) !== JSON.stringify(months);

    const isMonthChanged =
      state.selectedMonthDate !== undefined &&
      !Calendar.isMonthTheSameWithDate(state.currentMonth, state.selectedMonthDate);

    const isSelectedDateSlotChanged = selectedDateSlot !== state.selectedDateSlot;

    if (isApiCallFinished && Calendar.isWaitingForSlotsToLoad(months, state.selectedMonthDate)) {
      apiCallFinished(false);
    } else if (
      !isApiCallFinished &&
      !Calendar.isWaitingForSlotsToLoad(months, state.selectedMonthDate)
    ) {
      apiCallFinished(true);
    }

    const getMonthInViewIndex = () => {
      if (isMonthChanged && Calendar.getMonthByDate(months, state.selectedMonthDate)) {
        const nextMonthDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(
          months[months.length - 1].dates[0].date,
          1,
        );

        if (
          !Calendar.getMonthByDate(months, state.selectedMonthDate, 1) &&
          dateUtils.isDateInCalendarRange(nextMonthDate, maxAdvance)
        ) {
          if (loadNextSlots) {
            loadNextSlots(nextMonthDate, eligibleSlotsFilter).then(() => {
              apiCallFinished(true);
            }, apiHelpers.handleCustomApiErrors);
          }
        }

        const previousMonthDate = dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(
          months[0].dates[0].date,
          -1,
        );

        if (
          !Calendar.getMonthByDate(months, state.selectedMonthDate, -1) &&
          dateUtils.isDateInCalendarRange(previousMonthDate, maxAdvance)
        ) {
          if (loadPreviousSlots) {
            loadPreviousSlots(previousMonthDate, eligibleSlotsFilter).then(() => {
              apiCallFinished(true);
            }, apiHelpers.handleCustomApiErrors);
          }
        }

        return state.selectedMonthDate;
      }
      return new Date(state.currentMonth.year, state.currentMonth.month);
    };

    if (isApiCallFinished && (isMonthChanged || loadNewMonthsSlots || isSelectedDateSlotChanged)) {
      const displayedMonthDate =
        state.currentMonth && Calendar.getMonthIndexByMonth(months, state.currentMonth) !== -1
          ? getMonthInViewIndex()
          : new Date(selectedDateSlot);

      const currentMonth =
        isMonthChanged || loadNewMonthsSlots || !selectedDateSlot
          ? Calendar.getMonthByDate(months, displayedMonthDate)
          : months.find((month) => findDateSlot(month.dates, selectedDateSlot));

      updateAppointmentsDetailsWithAvailability({
        showingDatesFor: selectedPartnerId ? 'individual advisor' : 'all advisors',
        slotsAvailable:
          currentMonth && currentMonth.dates
            ? currentMonth.dates.filter((date) => date.times && date.times.length > 0).length
            : 0,
        dateRange: Calendar.getMonthIndexByDate(months, displayedMonthDate) + 1,
      });
      return {
        ...state,
        months,
        currentMonth,
        selectedDateSlot,
        selectedMonthDate: Calendar.equalsDate(displayedMonthDate, state.selectedMonthDate)
          ? undefined
          : state.selectedMonthDate,
      };
    }
    return state;
  }

  switchMonthBy = (increaseIndex) => {
    const { currentMonth } = this.state;

    triggerAvailabilityInteractionAnalytics('calendarChange');
    this.setState({
      selectedMonthDate: new Date(currentMonth.year, currentMonth.month + increaseIndex),
    });
  };

  onPreviousHandler = () => {
    this.switchMonthBy(-1);
  };

  onNextHandler = () => {
    this.switchMonthBy(1);
  };

  render() {
    const { currentMonth } = this.state;
    const { selectedDateSlot, selectDateSlot, maxAdvance } = this.props;

    if (!currentMonth) {
      return null;
    }

    const { month, year, dates } = currentMonth;
    const classNames = [styles.Calendar, selectedDateSlot ? styles.Calendar_selected : ''].join(
      ' ',
    );

    const firstIncludedMonth = new Date();
    const lastIncludedDate = new Date(firstIncludedMonth.getTime() + maxAdvance);

    const lastIncludedMonth = dateUtils.getDateFromString(
      dateUtils.getDateWithFirstDayOfMonthAndMonthOffset(
        dateUtils.getFormattedDate(lastIncludedDate),
      ),
    );
    const isPrevButtonDisabled =
      year <= firstIncludedMonth.getFullYear() && month <= firstIncludedMonth.getMonth();
    const isNextButtonDisabled =
      year >= lastIncludedMonth.getFullYear() && month >= lastIncludedMonth.getMonth();

    return (
      <section className={classNames} data-testid="calendar">
        <CalendarHeader
          month={month}
          year={year}
          onPrevious={(!isPrevButtonDisabled && this.onPreviousHandler) || null}
          onNext={(!isNextButtonDisabled && this.onNextHandler) || null}
        />
        <div className={styles.Calendar__body}>
          <CalendarWeek weekDays={dateUtils.DAYS_NAMES} />
          <CalendarDateSlots
            dates={dates}
            selectedDateSlot={selectedDateSlot}
            selectDateSlot={selectDateSlot}
          />
        </div>
      </section>
    );
  }
}
